// @ts-check

import { AppRuntime } from '../app/app_runtime.js?v=20260706d';
import { S } from '../app/app_state.js?v=20260817c';
import { escapeHtml } from '../utils/html_utils.js?v=20260706d';
import { setStatus } from '../render/plan_renderer.js?v=20260904f';
import {
  assetLibraryItems,
  beginObjectAssetPlacement,
  bindSelectedObjectAssetProperties,
  cancelObjectAssetPlacement,
  objectAssetLibraryError,
  refreshObjectAssetLibrary,
  renderSelectedObjectAssetProperties
} from './object_asset_system.js?v=20260903c';
import * as SystemRegistry from '../registry/system_registry.js?v=20260904f';
import { t } from '../i18n/i18n_service.js?v=20260722a';

const OBJECT_LIBRARY_TOOL = 'object_library';

function ensureUi() {
  if (!S.ui || typeof S.ui !== 'object') S.ui = {};
  return S.ui;
}

function objectLibraryItems() {
  const systemItems = SystemRegistry.collectObjectLibraryItems({ activeSystemOnly: true })
    .map(item => ({
      ...item,
      libraryId: `system:${item.systemId}:${item.id || item.action}`,
      librarySource: 'system'
    }));
  const assetItems = assetLibraryItems()
    .map(item => ({
      ...item,
      libraryId: `asset:${item.id}`,
      librarySource: 'asset'
    }));
  return [...systemItems, ...assetItems];
}

function objectLibraryRowThumbHtml(item) {
  if (item.librarySource !== 'asset') return '';
  if (item.thumbnailPath) {
    const transform = (item.thumbnailTransform || [1, 0, 0, 1, 0, 0]).map(Number).join(',');
    return `<img class="object-library-row-thumb" src="${escapeHtml(item.thumbnailPath)}" style="transform:matrix(${transform})" title="${item.thumbnailLegacy ? 'Bisherige Vorschau – aktualisierte Draufsicht noch nicht verfügbar' : 'Draufsicht'}" alt="" loading="lazy">`;
  }
  return '<span class="object-library-row-thumb object-library-row-thumb-empty" aria-hidden="true"></span>';
}

function objectLibraryRowsHtml(items = []) {
  const query = String(ensureUi().objectLibrarySearch || '').trim().toLocaleLowerCase();
  const filtered = items.filter(item => !query || `${item.label || ''} ${item.description || ''}`.toLocaleLowerCase().includes(query));
  const rows = filtered.map(item => `
    <button type="button" class="object-library-row" data-object-library-id="${escapeHtml(item.libraryId || item.id)}">
      ${objectLibraryRowThumbHtml(item)}
      <span class="object-library-row-text">
        <span class="object-library-row-title">${escapeHtml(item.label)}</span>
        ${item.description ? `<span class="object-library-row-meta">${escapeHtml(item.description)}</span>` : ''}
      </span>
    </button>
  `).join('');
  return rows || `<div class="empty object-library-empty">${escapeHtml(objectAssetLibraryError()
    || (query ? 'Keine passenden Objekte gefunden.' : t('sidebar_rechts.objektbibliothek.wert.leer', 'Noch keine Objekte verfuegbar.')))}</div>`;
}

function objectLibraryPanelHtml(items = []) {
  return `
    <div class="card object-library-panel">
      <div class="card-title">${escapeHtml(t('sidebar_rechts.objektbibliothek.titel.karte', 'Objekte'))}</div>
      <input id="objectLibrarySearch" type="search" aria-label="Objekte suchen" placeholder="Objekte suchen …" value="${escapeHtml(ensureUi().objectLibrarySearch || '')}" class="object-library-search">
      <div class="object-library-list">
        ${objectLibraryRowsHtml(items)}
      </div>
      ${renderSelectedObjectAssetProperties()}
    </div>
  `;
}

function renderObjectLibraryPanel() {
  if (!objectLibraryPanelActive()) return false;
  const host = document.getElementById('selectionPanel');
  if (!host) return false;
  host.className = 'object-library-selection-panel';
  host.innerHTML = objectLibraryPanelHtml(objectLibraryItems());
  bindObjectLibraryPanelEvents(host);
  return true;
}

function bindObjectLibraryPanelEvents(host) {
  const search = host.querySelector('#objectLibrarySearch');
  search?.addEventListener('input', () => {
    ensureUi().objectLibrarySearch = search.value;
    const list = host.querySelector('.object-library-list');
    list.innerHTML = objectLibraryRowsHtml(objectLibraryItems());
    bindObjectLibraryRows(list);
  });
  bindObjectLibraryRows(host);
  bindSelectedObjectAssetProperties();
}

function bindObjectLibraryRows(host) {
  const itemsById = new Map(objectLibraryItems().map(item => [item.libraryId || item.id, item]));
  host.querySelectorAll('[data-object-library-id]').forEach(button => {
    button.addEventListener('click', () => {
      beginObjectLibraryPlacement(itemsById.get(button.dataset.objectLibraryId || ''));
    });
  });
}

function beginObjectLibraryPlacement(item) {
  if (!item) return false;
  if (item.librarySource === 'system') {
    const action = item.action || item.tool || '';
    if (!item.systemId || !action) return false;
    cancelObjectAssetPlacement();
    ensureUi().showObjectLibraryPanel = false;
    syncObjectLibraryToolbarButton();
    return SystemRegistry.handleToolAction(item.systemId, action, item.payload || item) === true;
  }
  if (beginObjectAssetPlacement(item.id || '')) {
    setStatus('Objekt im Plan platzieren');
    return true;
  }
  return false;
}

function syncObjectLibraryToolbarButton() {
  const active = objectLibraryPanelActive();
  const button = document.getElementById('btnObjectLibrary');
  button?.classList.toggle('is-active', active);
  button?.setAttribute('aria-pressed', active ? 'true' : 'false');
}

function objectLibraryPanelActive() {
  return ensureUi().showObjectLibraryPanel === true;
}

function activateObjectLibraryPanel() {
  const ui = ensureUi();
  const nextActive = ui.showObjectLibraryPanel !== true;
  ui.showObjectLibraryPanel = nextActive;
  ui.showAppOptions = false;
  ui.showAppAdminPanel = false;
  if (nextActive) {
    ui.obstacleTool = null;
    setStatus('Objektauswahl geoeffnet');
    refreshObjectAssetLibrary({ force: true }).then(() => {
      if (objectLibraryPanelActive()) AppRuntime.refresh(false);
    });
  } else {
    cancelObjectAssetPlacement();
    setStatus('Objektauswahl geschlossen');
  }
  syncObjectLibraryToolbarButton();
  AppRuntime.refresh(false);
  return true;
}

function closeObjectLibraryPanel() {
  const ui = ensureUi();
  if (ui.showObjectLibraryPanel !== true) {
    syncObjectLibraryToolbarButton();
    return false;
  }
  ui.showObjectLibraryPanel = false;
  cancelObjectAssetPlacement();
  syncObjectLibraryToolbarButton();
  return true;
}

export {
  OBJECT_LIBRARY_TOOL,
  activateObjectLibraryPanel,
  closeObjectLibraryPanel,
  objectLibraryPanelActive,
  renderObjectLibraryPanel,
  syncObjectLibraryToolbarButton
};
